// src/phase4/ui/PhaserUIManager.ts
// Phaser UI Manager - Central coordinator for all Phaser UI components
// Manages panel visibility, callbacks, and UI state

import Phaser from 'phaser';
import { TowerSelectPanel } from './TowerSelectPanel';
import { TowerUpgradePanel, TowerPanelInfo, UpgradeInfo } from './TowerUpgradePanel';
import { WaveInfoPanel } from './WaveInfoPanel';
import { MoneyDisplay } from './MoneyDisplay';
import { HealthDisplay } from './HealthDisplay';
import { TowerType } from '../types/tower';

// UI State
interface UIState {
  currentPanel: 'none' | 'select' | 'upgrade' | 'wave';
  selectedBaseId: string | null;
  selectedTowerId: string | null;
}

export class PhaserUIManager {
  private scene: Phaser.Scene;
  private uiContainer: Phaser.GameObjects.Container;

  // UI Components
  private towerSelectPanel?: TowerSelectPanel;
  private towerUpgradePanel?: TowerUpgradePanel;
  private waveInfoPanel?: WaveInfoPanel;
  private moneyDisplay?: MoneyDisplay;
  private healthDisplay?: HealthDisplay;

  // Callbacks
  private onTowerPlaced?: (baseId: string, towerType: TowerType, pathName?: string) => void;
  private onTowerUpgraded?: (towerId: string) => void;
  private onTowerSold?: (towerId: string) => void;
  private onStartWave?: () => void;
  private onPanelClosed?: () => void;

  // State
  private state: UIState = {
    currentPanel: 'none',
    selectedBaseId: null,
    selectedTowerId: null,
  };

  constructor(scene: Phaser.Scene, uiContainer: Phaser.GameObjects.Container) {
    this.scene = scene;
    this.uiContainer = uiContainer;
    this.createAllUI();
    this.setupEventListeners();
  }

  private createAllUI(): void {
    // Create UI components (hidden by default except persistent displays)
    this.moneyDisplay = new MoneyDisplay(this.scene, 70, 40);
    this.healthDisplay = new HealthDisplay(this.scene, 720, 40);
    this.waveInfoPanel = new WaveInfoPanel(this.scene, 400, 560);
    this.towerSelectPanel = new TowerSelectPanel(this.scene, 0, 0);
    this.towerUpgradePanel = new TowerUpgradePanel(this.scene, 0, 0);

    // Initial hide for modal panels
    this.towerSelectPanel?.hide();
    this.towerUpgradePanel?.hide();

    // Add to container
    this.uiContainer.add([
      this.moneyDisplay,
      this.healthDisplay,
      this.waveInfoPanel,
      this.towerSelectPanel,
      this.towerUpgradePanel,
    ]);

    // Setup panel callbacks
    this.towerSelectPanel?.setOnSelectTower((baseId, type) => {
      this.onTowerPlaced?.(baseId, type);
      this.closeCurrentPanel();
    });

    this.towerSelectPanel?.setOnCancel(() => {
      this.closeCurrentPanel();
    });

    this.towerUpgradePanel?.setOnUpgrade((towerId) => {
      this.onTowerUpgraded?.(towerId);
    });

    this.towerUpgradePanel?.setOnSell((towerId) => {
      this.onTowerSold?.(towerId);
      this.closeCurrentPanel();
    });

    this.towerUpgradePanel?.setOnCancel(() => {
      this.closeCurrentPanel();
    });

    this.waveInfoPanel?.setOnStartWave(() => {
      this.onStartWave?.();
    });
  }

  private setupEventListeners(): void {
    // Handle escape key to close panels
    this.scene.input.keyboard?.on('keydown-ESCAPE', () => {
      if (this.state.currentPanel !== 'none') {
        this.closeCurrentPanel();
      }
    });
  }

  // ==================== Panel Management ====================

  showTowerSelect(baseId: string, x: number, y: number): void {
    this.closeCurrentPanel();
    this.towerSelectPanel?.show(baseId, x, y);
    this.state.currentPanel = 'select';
    this.state.selectedBaseId = baseId;
  }

  showTowerUpgrade(towerId: string, x: number, y: number, info: TowerPanelInfo, upgrade: UpgradeInfo): void {
    // If the same tower is already selected and panel is open, just update info
    if (this.state.currentPanel === 'upgrade' && this.state.selectedTowerId === towerId) {
      this.towerUpgradePanel?.updateInfo(info, upgrade);
      return;
    }

    // Different tower or no panel open - close current and show new
    this.closeCurrentPanel();
    this.towerUpgradePanel?.show(towerId, x, y);
    this.towerUpgradePanel?.updateInfo(info, upgrade);
    this.state.currentPanel = 'upgrade';
    this.state.selectedTowerId = towerId;
  }

  hideTowerUpgrade(): void {
    if (this.state.currentPanel === 'upgrade') {
      this.towerUpgradePanel?.hide();
      this.state.currentPanel = 'none';
      this.state.selectedTowerId = null;
      this.onPanelClosed?.();
    }
  }

  closeCurrentPanel(): void {
    if (this.state.currentPanel === 'select') {
      this.towerSelectPanel?.hide();
    } else if (this.state.currentPanel === 'upgrade') {
      this.towerUpgradePanel?.hide();
    }

    this.state.currentPanel = 'none';
    this.state.selectedBaseId = null;
    this.state.selectedTowerId = null;
    this.onPanelClosed?.();
  }

  // ==================== Display Updates ====================

  updateMoney(amount: number): void {
    this.moneyDisplay?.update(amount);
  }

  updateHealth(value: number, max: number): void {
    this.healthDisplay?.update(value, max);
  }

  updateWaveInfo(current: number, total: number, enemyCount: number = 0): void {
    this.waveInfoPanel?.update(current, total, enemyCount);
  }

  setWaveActive(active: boolean): void {
    this.waveInfoPanel?.setWaveActive(active);
  }

  // ==================== Callback Setters ====================

  setTowerPlacedCallback(cb: (baseId: string, towerType: TowerType, pathName?: string) => void): void {
    this.onTowerPlaced = cb;
  }

  setTowerUpgradedCallback(cb: (towerId: string) => void): void {
    this.onTowerUpgraded = cb;
  }

  setTowerSoldCallback(cb: (towerId: string) => void): void {
    this.onTowerSold = cb;
  }

  setOnStartWaveCallback(cb: () => void): void {
    this.onStartWave = cb;
    this.waveInfoPanel?.setOnStartWave(cb);
  }

  setOnPanelClosedCallback(cb: () => void): void {
    this.onPanelClosed = cb;
  }

  // ==================== State Queries ====================

  isPanelOpen(): boolean {
    return this.state.currentPanel !== 'none';
  }

  getCurrentPanel(): string {
    return this.state.currentPanel;
  }

  // ==================== Cleanup ====================

  destroy(): void {
    this.moneyDisplay?.destroy();
    this.healthDisplay?.destroy();
    this.waveInfoPanel?.destroy();
    this.towerSelectPanel?.destroy();
    this.towerUpgradePanel?.destroy();

    this.moneyDisplay = undefined;
    this.healthDisplay = undefined;
    this.waveInfoPanel = undefined;
    this.towerSelectPanel = undefined;
    this.towerUpgradePanel = undefined;
  }
}

export default PhaserUIManager;
